import type { ConsortiumCategory } from '@/types'

import { isValidTerm, simulateConsortium } from '@/lib/consortium'
import { maskPhone, onlyDigits } from '@/lib/format'
import { getConsortiumCategory } from '@/constants/consorcios'

export type LeadField = 'name' | 'phone' | 'email' | 'category' | 'creditValue' | 'message' | 'consent'

export type LeadErrors = Partial<Record<LeadField, string>>

export interface LeadData {
    name: string
    phone: string
    email: string
    category: ConsortiumCategory
    creditValue: number | null
    termMonths: number | null
    installmentEstimate: number | null
    message: string | null
    sourcePage: string
}

interface LeadValidationResult {
    errors: LeadErrors
    data: LeadData | null
}

export const LEAD_MESSAGE_MAX_LENGTH = 1000

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const hasControlCharacters = (value: string, allowLineBreaks = false): boolean => {
    for (const character of value) {
        const code = character.charCodeAt(0)

        if (allowLineBreaks && (code === 10 || code === 13)) continue
        if (code < 32 || code === 127) return true
    }

    return false
}

const toInteger = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value)
    if (typeof value === 'string' && onlyDigits(value)) return Number(onlyDigits(value))

    return null
}

export const cleanSourcePage = (value: unknown): string => {
    if (typeof value !== 'string' || !value.startsWith('/')) return '/'

    return value.split(/[?#]/)[0].slice(0, 120) || '/'
}

export const validateLead = (input: Record<string, unknown>): LeadValidationResult => {
    const errors: LeadErrors = {}

    const name = typeof input.name === 'string' ? input.name.trim().replace(/\s+/g, ' ') : ''

    if (name.length < 2) errors.name = 'Informe seu nome.'
    else if (name.length > 100 || hasControlCharacters(name)) errors.name = 'Use até 100 caracteres, só com letras e espaços.'

    const phoneDigits = typeof input.phone === 'string' ? onlyDigits(input.phone) : ''

    if (phoneDigits.length < 10 || phoneDigits.length > 11) errors.phone = 'Informe o WhatsApp com DDD.'

    const email = typeof input.email === 'string' ? input.email.trim().toLowerCase() : ''

    if (!email) errors.email = 'Informe seu e-mail.'
    else if (email.length > 254 || !EMAIL_PATTERN.test(email)) errors.email = 'Confira o e-mail informado.'

    const category = typeof input.category === 'string' ? getConsortiumCategory(input.category) : undefined

    if (!category) errors.category = 'Escolha o tipo de consórcio.'

    const hasCredit = input.creditValue !== null && input.creditValue !== undefined && input.creditValue !== ''
    const creditValue = hasCredit ? toInteger(input.creditValue) : null

    if (hasCredit && (creditValue === null || creditValue < 1000 || creditValue > 50000000)) errors.creditValue = 'Informe um valor entre R$ 1.000 e R$ 50.000.000.'

    const message = typeof input.message === 'string' ? input.message.trim() : ''

    if (message.length > LEAD_MESSAGE_MAX_LENGTH) errors.message = `Use até ${LEAD_MESSAGE_MAX_LENGTH} caracteres.`
    else if (hasControlCharacters(message, true)) errors.message = 'Remova os caracteres especiais da mensagem.'

    if (input.consent !== true) errors.consent = 'Para continuar, aceite a política de privacidade.'

    if (Object.keys(errors).length > 0 || !category) return { errors, data: null }

    const requestedTerm = toInteger(input.termMonths)
    const termMonths = requestedTerm !== null && isValidTerm(category, requestedTerm) ? requestedTerm : null
    const installmentEstimate = creditValue && termMonths
        ? Math.round(simulateConsortium(category, creditValue, termMonths).installment * 100) / 100
        : null

    return {
        errors,
        data: {
            name,
            phone: maskPhone(phoneDigits),
            email,
            category,
            creditValue,
            termMonths,
            installmentEstimate,
            message: message || null,
            sourcePage: cleanSourcePage(input.sourcePage)
        }
    }
}
